import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CalendarDays, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/lib/supabaseClient';
import { formatAttendanceHours } from '@/lib/attendanceHours';

const ROME_TIME_ZONE = 'Europe/Rome';

function romeToday() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ROME_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function monthEnd(month) {
  const match = /^(\d{4})-(\d{2})$/.exec(month || '');
  if (!match) return '';
  const last = new Date(Date.UTC(Number(match[1]), Number(match[2]), 0));
  return `${match[1]}-${match[2]}-${String(last.getUTCDate()).padStart(2, '0')}`;
}

function formatWorkday(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: ROME_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function isOfficialClosed(record) {
  return record?.status === 'CHIUSA'
    && record.hours_seconds !== null
    && record.hours_seconds !== undefined
    && Number.isFinite(Number(record.hours_seconds))
    && Number(record.hours_seconds) >= 0;
}

function statusLabel(record) {
  if (record?.status === 'APERTA') return 'Aperta';
  if (record?.status === 'CHIUSA' && isOfficialClosed(record)) return 'Chiusa';
  return 'Da verificare';
}

function recordNote(record) {
  if (record?.administratively_corrected === true || record?.corrected === true) {
    return 'Corretta dall’amministrazione';
  }
  if (record?.anomaly === true || (record?.status === 'CHIUSA' && !isOfficialClosed(record))) {
    return 'Durata da verificare';
  }
  return '';
}

function companyLocation(record) {
  return [record?.company_name_snapshot, record?.location_name_snapshot]
    .filter((value) => value && value !== '—')
    .join(' · ') || 'Locale non indicato';
}

function Status({ record }) {
  const label = statusLabel(record);
  const tone = label === 'Chiusa'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : label === 'Aperta'
      ? 'border-blue-200 bg-blue-50 text-blue-800'
      : 'border-amber-200 bg-amber-50 text-amber-800';
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>
      {label}
    </span>
  );
}

function AttendanceCells({ record }) {
  const note = recordNote(record);
  return (
    <>
      <td className="px-3 py-3 align-top text-sm font-medium text-brand-navy">{formatWorkday(record.local_date)}</td>
      <td className="px-3 py-3 align-top text-sm text-stone-700">{formatDateTime(record.entered_at_utc)}</td>
      <td className="px-3 py-3 align-top text-sm text-stone-700">{formatDateTime(record.exited_at_utc)}</td>
      <td className="px-3 py-3 align-top text-sm text-stone-700">{companyLocation(record)}</td>
      <td className="px-3 py-3 align-top text-sm font-semibold text-brand-navy">
        {isOfficialClosed(record) ? formatAttendanceHours(record.hours_seconds) : '—'}
      </td>
      <td className="px-3 py-3 align-top text-sm">
        <div className="flex flex-col items-start gap-1">
          <Status record={record} />
          {note && <span className="text-xs text-stone-500">{note}</span>}
        </div>
      </td>
    </>
  );
}

export default function EmployeeAttendanceHistory() {
  const today = useMemo(romeToday, []);
  const [filterMode, setFilterMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(today.slice(0, 7));
  const [dateFrom, setDateFrom] = useState(`${today.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/api/attendance/me');
      if (!Array.isArray(response)) throw new Error('invalid_response');
      setRecords(response);
    } catch {
      setError('Storico non disponibile. Controlla la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const rangeInvalid = filterMode === 'range'
    && (!dateFrom || !dateTo || dateFrom > dateTo);
  const rangeStart = filterMode === 'month' ? `${selectedMonth}-01` : dateFrom;
  const rangeEnd = filterMode === 'month' ? monthEnd(selectedMonth) : dateTo;
  const filteredRecords = useMemo(() => {
    if (rangeInvalid || !rangeStart || !rangeEnd) return [];
    return records
      .filter((record) => {
        const workday = String(record?.local_date || '').slice(0, 10);
        return workday && workday >= rangeStart && workday <= rangeEnd;
      })
      .slice()
      .sort((left, right) => String(right.entered_at_utc || '').localeCompare(String(left.entered_at_utc || '')));
  }, [rangeEnd, rangeInvalid, rangeStart, records]);

  return (
    <main className="mx-auto w-full max-w-5xl px-3 pb-28 pt-4 sm:px-5 md:px-6" data-testid="employee-attendance-history">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-green">Area personale</p>
          <h1 className="mt-1 font-display text-2xl font-semibold text-brand-navy">Presenze</h1>
          <p className="mt-1 text-sm text-stone-500">Il tuo storico personale, per data lavorativa.</p>
        </div>
        <Link
          to="/foundation"
          className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 text-sm font-semibold text-brand-navy hover:bg-stone-50"
          data-testid="employee-attendance-dashboard-link"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Dashboard
        </Link>
      </header>

      <section className="mb-4 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm sm:p-4" aria-label="Filtri presenze">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs font-semibold text-stone-600">
            Periodo
            <select
              value={filterMode}
              onChange={(event) => setFilterMode(event.target.value)}
              className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-medium text-brand-navy"
              data-testid="employee-attendance-filter-mode"
            >
              <option value="month">Mese</option>
              <option value="range">Intervallo</option>
            </select>
          </label>
          {filterMode === 'month' ? (
            <label className="space-y-1 text-xs font-semibold text-stone-600">
              Mese
              <span className="relative block">
                <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden="true" />
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  className="h-10 w-full rounded-xl border border-stone-200 bg-white pl-9 pr-3 text-sm font-medium text-brand-navy"
                  data-testid="employee-attendance-month"
                />
              </span>
            </label>
          ) : (
            <>
              <label className="space-y-1 text-xs font-semibold text-stone-600">
                Dal
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                  className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-medium text-brand-navy"
                  data-testid="employee-attendance-date-from"
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-stone-600">
                Al
                <input
                  type="date"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                  className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-medium text-brand-navy"
                  data-testid="employee-attendance-date-to"
                />
              </label>
            </>
          )}
        </div>
        {rangeInvalid && <p className="mt-2 text-sm text-rose-700" role="alert">Seleziona un intervallo valido.</p>}
      </section>

      {error ? (
        <section className="rounded-2xl border border-rose-200 bg-white p-5 text-center" role="alert">
          <p className="text-sm text-rose-800">{error}</p>
          <button
            type="button"
            onClick={loadRecords}
            disabled={loading}
            className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-xl border border-rose-200 px-4 text-sm font-semibold text-rose-900 disabled:opacity-60"
            data-testid="employee-attendance-retry"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Riprova
          </button>
        </section>
      ) : loading ? (
        <p className="rounded-2xl border border-stone-200 bg-white p-5 text-center text-sm text-stone-500" role="status">
          Caricamento presenze…
        </p>
      ) : filteredRecords.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-stone-300 bg-white p-6 text-center text-sm text-stone-500" data-testid="employee-attendance-empty">
          Nessuna presenza nel periodo selezionato.
        </p>
      ) : (
        <section aria-label="Storico personale" data-testid="employee-attendance-results">
          <p className="mb-2 text-xs text-stone-500">{filteredRecords.length} presenze nel periodo</p>
          <div className="hidden overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm md:block">
            <table className="w-full table-fixed border-collapse">
              <thead className="bg-stone-50 text-left text-xs font-semibold text-stone-600">
                <tr>
                  <th className="px-3 py-3">Data lavorativa</th>
                  <th className="px-3 py-3">Entrata</th>
                  <th className="px-3 py-3">Uscita</th>
                  <th className="px-3 py-3">Azienda / locale</th>
                  <th className="px-3 py-3">Ore ufficiali</th>
                  <th className="px-3 py-3">Stato o note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredRecords.map((record) => (
                  <tr key={record.id}><AttendanceCells record={record} /></tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="space-y-3 md:hidden">
            {filteredRecords.map((record) => (
              <li key={record.id} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="employee-attendance-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Data lavorativa</p>
                    <p className="mt-0.5 font-display text-lg font-semibold text-brand-navy">{formatWorkday(record.local_date)}</p>
                  </div>
                  <Status record={record} />
                </div>
                <dl className="mt-3 grid grid-cols-1 gap-2 border-t border-stone-100 pt-3 text-sm sm:grid-cols-2">
                  <div><dt className="text-xs text-stone-500">Entrata</dt><dd className="font-medium text-stone-800">{formatDateTime(record.entered_at_utc)}</dd></div>
                  <div><dt className="text-xs text-stone-500">Uscita</dt><dd className="font-medium text-stone-800">{formatDateTime(record.exited_at_utc)}</dd></div>
                  <div><dt className="text-xs text-stone-500">Azienda / locale</dt><dd className="font-medium text-stone-800">{companyLocation(record)}</dd></div>
                  <div><dt className="text-xs text-stone-500">Ore ufficiali</dt><dd className="font-semibold text-brand-navy">{isOfficialClosed(record) ? formatAttendanceHours(record.hours_seconds) : '—'}</dd></div>
                </dl>
                {recordNote(record) && <p className="mt-3 border-t border-stone-100 pt-2 text-xs text-stone-500">{recordNote(record)}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
